import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "../../../../../../components/Button";
import { RetryButton } from "../../../../../../components/RetryButton";
import { RichTextEditor } from "../../../../../../components/RichTextEditor";
import { Tag } from "../../../../../../components/Tag";
import { noteTypes } from "../../../../../../constants/noteTypes";
import { getNoteData } from "../../../../../../hooks/queries/notes";
import { useModal } from "../../../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  AssignedToLabel,
  EditButton,
  Container,
  NotAvailableContainer,
  NotAvailableMessage,
  SubmitContainer,
  TextAreaContainer,
  Title,
  Label,
  UrgentChipContainer,
} from "./ViewNoteModal.styles";

type ViewNoteModalProps = {
  noteId: number;
  onEditClick: (noteId: number) => void;
};

export function ViewNoteModal({ noteId, onEditClick }: ViewNoteModalProps) {
  const { t } = useTranslation();

  const [noteContent, setNoteContent] = useState("");
  const [labelContent, setLabelContent] = useState("");
  const { closeModal } = useModal();

  const { patientNoteData, isFetching, isError, refetch, isRefetching } =
    getNoteData(noteId);

  const titleText =
    patientNoteData?.noteType === noteTypes.TASK
      ? t("patientPage_notesTable_viewTask")
      : t("patientPage_notesTable_viewNote");

  useMemo(() => {
    if (!isFetching && !isError && !isRefetching && patientNoteData) {
      setNoteContent(patientNoteData.notes);
      const timeStr = getLongFormattedDate(patientNoteData.time);
      const labelStr = `${timeStr} - ${patientNoteData.reviewedByEmail}`;
      setLabelContent(labelStr);
    }
  }, [patientNoteData, isFetching, isError, isRefetching]);

  // RichTextEditor requires onValueChange callback even in readOnly mode
  // We use an empty function since we don't need to handle changes in view mode
  const handleInputChange = () => {
    // No-op: This is a read-only view
  };

  const handleEditNoteClose = async () => {
    await closeModal();
    onEditClick(noteId);
  };

  const refreshData = () => {
    refetch();
  };

  const handleDoneClick = () => {
    closeModal();
  };

  if (isError) {
    return (
      <Container>
        <p>
          <Trans
            i18nKey="patientPage_note_loadError"
            components={{
              retry: <RetryButton onClick={refreshData} />,
            }}
          />
        </p>
      </Container>
    );
  }

  // API interceptor swallows 4xx, so a missing note resolves as a successful
  // query with an error body. Detect it by the absent id rather than relying
  // on isError, then show a deep-link-friendly message.
  if (!isFetching && !patientNoteData?.id) {
    return (
      <NotAvailableContainer>
        <Title>{titleText}</Title>
        <NotAvailableMessage>
          {t("patientPage_notes_unavailable")}
        </NotAvailableMessage>
        <SubmitContainer>
          <Button
            label={t("patientPage_notesTable_done")}
            type="button"
            onClick={handleDoneClick}
          />
        </SubmitContainer>
      </NotAvailableContainer>
    );
  }

  if (isFetching) {
    return (
      <Container>
        <Title>{titleText}</Title>
        <Label>{labelContent}</Label>
        <TextAreaContainer>
          <div style={{ height: "300px" }}> </div>
        </TextAreaContainer>
        <SubmitContainer>
          <Button
            label={t("patientPage_notesTable_done")}
            type="button"
            onClick={handleDoneClick}
          />
          <EditButton onClick={handleEditNoteClose}>
            {t("patientPage_notesTable_edit")}
          </EditButton>
        </SubmitContainer>
      </Container>
    );
  }

  const isTask = patientNoteData.noteType === noteTypes.TASK;
  const assignedHcp = patientNoteData.assignedHcpName ?? null;

  return (
    <Container>
      <Title>{titleText}</Title>
      <Label>{labelContent}</Label>
      {isTask && assignedHcp && (
        <AssignedToLabel>
          {t("patientPage_notes_assignedTo", { hcp: assignedHcp })}
        </AssignedToLabel>
      )}
      {isTask && patientNoteData.isUrgent && !patientNoteData.isCompleted && (
        <UrgentChipContainer>
          <Tag variant="error">{t("patientPage_notesTable_urgent")}</Tag>
        </UrgentChipContainer>
      )}
      <TextAreaContainer>
        <RichTextEditor
          value={noteContent}
          placeholderTxt={t("patientPage_notesTable_notePlaceholder")}
          onValueChange={handleInputChange}
          height={300}
          readOnly
        />
      </TextAreaContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_notesTable_done")}
          type="button"
          onClick={handleDoneClick}
        />
        {patientNoteData.editable && (
          <EditButton onClick={handleEditNoteClose}>
            {t("patientPage_notesTable_edit")}
          </EditButton>
        )}
      </SubmitContainer>
    </Container>
  );
}
